import { resolveMoneyDecimals, roundMoney, subtractMoney, sumMoney } from "../utils/money";
import { formatNumberByCountry } from "../utils/formatNumbers";
import { llevaImpuestoDeBolsa, totalDeImpuestoDeBolsas } from "../utils/impuestoDeBolsas";

/**
 * Si las líneas que tiene Business Central son las que este terminal le mandó.
 *
 * Existe porque reenviar las líneas no las corregía: `insertaLineasFacturasVenta`
 * sólo insertaba, y sobre una línea que ya existía respondía `Ok` sin tocarla.
 * Medido en Costa Rica DEV el 14/09/2026, sobre el borrador `VF15-009099`: dos
 * líneas por 8.000; la primera, reenviada con cantidad 2 y el mismo `Line_No_`,
 * siguió en 8.000 cuando tenía que quedar en 13.000, y la respuesta fue `Ok`.
 *
 * El escenario real es una cabecera cuya respuesta se perdió: la venta se edita,
 * la corrida siguiente adopta el borrador y reenvía las líneas nuevas, y BC
 * conserva las viejas. Si BC tiene menos, la venta se registra y se timbra por
 * menos de lo cobrado. La BC 1.0.4.112 actualiza y retira líneas con
 * `"Reemplazar_Lineas": true`, pero una BC anterior ignora la clave, y es esta
 * regla la que lo detecta.
 *
 * Corre entre las líneas y los pagos, sobre la lectura de `estadoFactura` que ya
 * se hacía para ajustar el último pago, y bloquea si:
 *
 *     numLineas ≠ líneas locales
 *     | |importeLineas| − |Σ pagos locales| | > max(0,05; toleranciaPagos × numLineas)
 *
 * La comparación de importes va **en los dos sentidos**: BC con más y BC con
 * menos. La referencia es la suma de **todas** las líneas de pago locales,
 * deducidas incluidas, porque un pago local no lleva vuelto --lo recibido y el
 * cambio se guardan aparte y no viajan-- y la venta no se cierra con más de
 * 0,02 de pendiente: Σ pagos locales es el total local.
 *
 * El umbral nunca baja de 0,05, así que una diferencia de redondeo que el ajuste
 * de la última forma de pago absorbe nunca bloquea. Entre 0,05 y
 * `toleranciaPagos` × `numLineas` --sólo con divisas de precisión gruesa-- sigue
 * el aviso de siempre.
 *
 * **Sin pagos que viajen** --una nota de crédito sobre un histórico sin detalle
 * de pago, con todas sus formas de pago deducidas-- la regla actúa igual. El
 * daño que evita no está en los pagos sino en registrar un documento con líneas
 * que no son las del terminal, y las deducidas suman el total local lo mismo que
 * un pago que viaja.
 *
 * Ante cualquier duda no se bloquea --respuesta nula, documento que no está en
 * borrador, o una BC que no manda `numLineas` o `importeLineas`--: se sigue como
 * siempre. Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H1 · TPV, con
 * las precisiones de las rondas 4 y 5.
 */

/** Lo mínimo que tiene que diferir el importe para bloquear. */
export const UMBRAL_MINIMO_DE_LINEAS = 0.05;

/** Un número de la respuesta, o `null` si no viene o no es un número. */
const numeroDe = (valor) => {
  if (valor === null || valor === undefined || String(valor).trim() === "") return null;
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
};

/**
 * Lo que no cuadra entre las líneas de Business Central y las del terminal, o
 * `null` si no hay que frenar.
 *
 * @param {object|null} estado Lo que devolvió `consultarEstadoFactura`.
 * @param {{lineasLocales: Array, pagosLocales: Array}} locales
 *   Las líneas que viajan y **todas** las líneas de pago, deducidas incluidas.
 * @param {object} country El país activo, para la precisión de cálculo.
 * @returns {{lineasBC: number, importeBC: number, lineasTerminal: number,
 *   importeTerminal: number, diferencia: number, umbral: number}|null}
 */
export const lineasQueNoCuadranEnBC = (estado, { lineasLocales, pagosLocales } = {}, country) => {
  if (!estado || typeof estado !== "object") return null;
  if (estado.estado !== "borrador") return null;

  const lineasBC = numeroDe(estado.numLineas);
  const importeLineas = numeroDe(estado.importeLineas);
  if (lineasBC === null || importeLineas === null) return null;

  const decimales = resolveMoneyDecimals(country);
  const lineasTerminal = (lineasLocales || []).length;
  const importeBC = Math.abs(roundMoney(importeLineas, decimales));
  const importeTerminal = Math.abs(sumMoney(pagosLocales || [], (p) => p?.Amount, decimales));

  // Una sola vez, y antes del ajuste de la última forma de pago.
  const diferencia = subtractMoney(importeBC, importeTerminal, decimales);

  // Sin `toleranciaPagos` el umbral es el mínimo: la BC que manda `numLineas`
  // ya manda la tolerancia, así que esto sólo cubre una respuesta incompleta.
  const tolerancia = Math.abs(numeroDe(estado.toleranciaPagos) ?? 0);
  const umbral = Math.max(UMBRAL_MINIMO_DE_LINEAS, roundMoney(tolerancia * lineasBC, decimales));

  // El impuesto a las bolsas de plástico (Perú) no viaja como línea, pero se
  // cobra, así que va en los pagos. Business Central lo calcula por su cuenta, y
  // según cómo lo haga puede sumar una línea por cada producto con bolsa, y su
  // importe puede ir o no en `importeLineas`. Se aceptan las dos formas, y sólo
  // cuando el documento lleva bolsas. Ver utils/impuestoDeBolsas.js.
  const lineasConBolsa = (lineasLocales || []).filter(llevaImpuestoDeBolsa).length;
  const impuestoDeBolsas = Math.abs(totalDeImpuestoDeBolsas(lineasLocales || [], country));
  const terminalSinElImpuesto = subtractMoney(importeTerminal, impuestoDeBolsas, decimales);
  const sinElImpuesto = subtractMoney(importeBC, terminalSinElImpuesto, decimales);

  const noCuadranLasLineas =
    lineasBC !== lineasTerminal && lineasBC !== lineasTerminal + lineasConBolsa;
  const noCuadraElImporte =
    Math.abs(diferencia) > umbral && (impuestoDeBolsas === 0 || Math.abs(sinElImpuesto) > umbral);
  if (!noCuadranLasLineas && !noCuadraElImporte) return null;

  return { lineasBC, importeBC, lineasTerminal, importeTerminal, diferencia, umbral };
};

/**
 * El motivo para la fila, con los importes formateados como los lee el país.
 *
 * @param {{lineasBC: number, importeBC: number, lineasTerminal: number, importeTerminal: number}} lineas
 * @param {object} country
 */
export const avisoDeLineasQueNoCuadran = (
  { lineasBC, importeBC, lineasTerminal, importeTerminal },
  country
) => {
  const importe = (valor) => formatNumberByCountry(valor, country) || String(valor);
  return (
    `La venta no se registró: Business Central tiene ${lineasBC} línea(s) por ${importe(importeBC)} ` +
    `y este terminal ${lineasTerminal} por ${importe(importeTerminal)}. ` +
    "Se volverán a enviar las líneas en la siguiente sincronización."
  );
};
